// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// 🔴 R · AS VIAGENS DE UMA LINHA DO MAPA — o que o período do lote apanha
// ============================================================================
//
// 📗 **Cliente, 2026-09-16:** *«o mover período tem de dar para mover não só uma viagem mas tem
//    de dar para seleccionar várias viagens… tipo 1 de Setembro a 20 de Outubro e passar tudo
//    para outra linha»*.
//
// ⚠️⚠️ **A REGRA É A MESMA DO `extentoDaCorrida`, e tinha de ser.** Ele responde a *«até onde vai
//    a viagem que está NESTE dia»*; isto responde a *«que viagens tem esta linha»*. A chave é a
//    mesma — a **corrida** (campo 10) e, na falta dela, o **itinerário** (campo 5) —, e a
//    fronteira é a mesma: um dia de oficina/avaria (campo 7) corta.
//
//    📌 **Se divergissem, o ecrã dizia «5 viagens» e o gesto movia outras.** É por isso que a
//    guarda `viagens-da-linha.test.ts` confronta as duas, dia a dia, sobre a mesma linha: para
//    todo o dia com serviço, o extento da viagem que contém esse dia tem de ser **exactamente**
//    o extento que `extentoDaCorrida` dá. Duas formulações da mesma regra divergem, e a que
//    divergir é a que ninguém corre.
//
// ⚠️ **Sem DOM e sem React** — é lógica pura sobre a grelha, e é isso que a torna verificável
//    sem navegador. O componente desenha o que isto diz; não decide nada.
//
// ⚠️ **NÃO conhece a viagem que PARTE num dia de rotação** (campo 13), e é deliberado: o lote
//    move a linha `principal`, que é o que `mudarExecutor` sabe mover quando se lhe dá a
//    referência. Uma viagem que só existe como `partida` num dia entra na lista **a partir do
//    dia seguinte**, que é onde ela é a `principal`. 🅿️ Declarado: mover a metade `partida` de
//    um dia de rotação dentro de um lote não está feito, e o servidor recusaria em voz alta
//    (`rotacao_exige_servico`) se o ecrã o tentasse.
// ============================================================================

import type { CelulaCompacta } from '@xlv-gva-mpt/dominio';
import type { ViagemDaLinha } from './previsao-do-gesto.js';

/** O que é preciso saber do plano para nomear as viagens — e mais nada. */
export interface TabelasDaLinha {
  readonly corridas: readonly string[];
  readonly clientes: readonly (readonly [sigla: string, nome: string, cor?: string])[];
}

/**
 * ⚠️ **A chave de continuidade — a MESMA de `extentoDaCorrida`.** A corrida quando existe, o
 * itinerário quando não; `-1` em ambos quer dizer «não é serviço», e aí a viagem não existe.
 */
function chave(c: CelulaCompacta): number {
  return c[10] >= 0 ? c[10] : c[5];
}

/**
 * As viagens desta linha da grelha, por ordem do primeiro dia.
 *
 * ⚠️ **Um dia com estado de viatura (campo 7) CORTA**, exactamente como em `extentoDaCorrida`:
 * uma oficina a meio não junta os dois lados numa viagem só.
 *
 * ⚠️ **Uma viagem sem referência entra na lista com `referencia: ''`.** Não se esconde: o lote
 * recusa-a pelo nome (`viagem_sem_referencia`, com a saída escrita), e escondê-la fazia o ecrã
 * dizer «3 viagens» sobre um período que tem 4 — *«a ausência não é prova»*.
 */
export function viagensDaLinha(
  linha: readonly (CelulaCompacta | null)[],
  tabelas: TabelasDaLinha,
): readonly ViagemDaLinha[] {
  const viagens: ViagemDaLinha[] = [];
  let d = 0;
  while (d < linha.length) {
    const c = linha[d];
    if (c == null || c[7] >= 0 || chave(c) < 0) {
      d += 1;
      continue;
    }
    const k = chave(c);
    let fim = d;
    while (fim + 1 < linha.length) {
      const seguinte = linha[fim + 1];
      if (seguinte == null || seguinte[7] >= 0 || chave(seguinte) !== k) break;
      fim += 1;
    }
    viagens.push({
      referencia: c[10] >= 0 ? (tabelas.corridas[c[10]] ?? '') : '',
      sigla: c[0] >= 0 ? (tabelas.clientes[c[0]]?.[0] ?? '') : '',
      cliente: c[0],
      dia0: d,
      dia1: fim,
    });
    d = fim + 1;
  }
  return viagens;
}
